import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { renderMarkdown } from '../utils/markdown';
import {
  Typography, Space, Tag, Spin, message as antMessage,
  Collapse, Tooltip, Segmented, Button, Input, Badge,
} from 'antd';
import {
  SendOutlined, RobotOutlined, UserOutlined, ToolOutlined,
  ThunderboltOutlined, GlobalOutlined, AppstoreOutlined, DatabaseOutlined,
  ReadOutlined, MessageOutlined, BuildOutlined,
  CheckCircleFilled, ClockCircleOutlined, MinusCircleOutlined, SaveOutlined,
  LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import { EXECUTOR_API, BUILDER_API } from '../types';
import { glass, glassBarStyle, glassDivider, glassInsetStyle } from '../theme';
import { GlassWorkspace, GlassPanel } from './common/GlassWorkspace';
import { readFlag, writeFlag } from '../utils/persistedFlag';

const TOOLS_COLLAPSED_KEY = 'playground.toolsCollapsed';

const { Text } = Typography;
const { Panel } = Collapse;

// ── Types ────────────────────────────────────────────────────────────────────

type PlaygroundMode = 'chat' | 'builder';
type ComponentKey = 'goal' | 'sense' | 'reason' | 'memory' | 'plan' | 'act' | 'coordinate';
type ComponentStatus = 'pending' | 'in_progress' | 'complete';

interface GoalSpec { objective?: string; success_criteria?: string[]; constraints?: string[] }
interface SenseSpec { input_type?: string; extract_contact?: boolean }
interface ReasonSpec { model?: string; output_schema?: string; extra_instructions?: string }
interface MemorySpec { short_term?: boolean; long_term_source?: string }
interface PlanSpec { available_actions?: string[] }
interface ActSpec { actions?: string[] }
interface CoordinateSpec { specialists?: string[] }

interface CognitiveSpec {
  goal?: GoalSpec;
  sense?: SenseSpec;
  reason?: ReasonSpec;
  memory?: MemorySpec;
  plan?: PlanSpec;
  act?: ActSpec;
  coordinate?: CoordinateSpec;
}

interface ToolMeta { name: string; description: string }
interface ToolGroup { id: string; name: string; type: string; source: 'env' | 'db' | 'builtin'; tools: ToolMeta[] }
interface ToolCall { name: string; args: Record<string, unknown>; result: unknown }
interface ChatMessage { role: 'user' | 'assistant'; content: string; tool_calls?: ToolCall[] }

// ── Connector style map ───────────────────────────────────────────────────────

const CONNECTOR_STYLE: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  monday:     { color: '#0073ea', icon: <AppstoreOutlined />, label: 'Monday.com' },
  web_search: { color: '#52c41a', icon: <GlobalOutlined />,   label: 'Web search' },
  mongodb:    { color: '#00ed64', icon: <DatabaseOutlined />, label: 'MongoDB' },
  brain:      { color: '#722ed1', icon: <ReadOutlined />,     label: 'Brain' },
};
function connectorStyle(type: string) {
  return CONNECTOR_STYLE[type] ?? { color: '#8c8c8c', icon: <ToolOutlined />, label: type };
}

// ── Cognitive spec component definitions ─────────────────────────────────────

const COGNITIVE_COMPONENTS: { key: ComponentKey; label: string; emoji: string; required: boolean }[] = [
  { key: 'goal',       label: 'Objetivo',     emoji: '🎯', required: true  },
  { key: 'sense',      label: 'Percepción',   emoji: '👁️', required: true  },
  { key: 'reason',     label: 'Razonamiento', emoji: '🧠', required: true  },
  { key: 'memory',     label: 'Memoria',      emoji: '💾', required: false },
  { key: 'plan',       label: 'Plan',         emoji: '📋', required: false },
  { key: 'act',        label: 'Acción',       emoji: '⚡', required: true  },
  { key: 'coordinate', label: 'Coordinación', emoji: '🔗', required: false },
];

function specSummary(key: ComponentKey, spec: CognitiveSpec): string | null {
  switch (key) {
    case 'goal':       return spec.goal?.objective ? `"${spec.goal.objective.slice(0, 55)}${spec.goal.objective.length > 55 ? '…' : ''}"` : null;
    case 'sense':      return spec.sense?.input_type ?? null;
    case 'reason':     return spec.reason ? `${spec.reason.model ?? 'gemini'} → ${spec.reason.output_schema ?? '?'}` : null;
    case 'memory':     return spec.memory ? (spec.memory.long_term_source && spec.memory.long_term_source !== 'none' ? `short + long (${spec.memory.long_term_source})` : 'short term') : null;
    case 'plan':       return spec.plan?.available_actions?.join(', ') ?? null;
    case 'act':        return spec.act?.actions?.join(', ') ?? null;
    case 'coordinate': return spec.coordinate?.specialists?.join(', ') ?? null;
    default:           return null;
  }
}

// ── CognitiveSpecPanel ────────────────────────────────────────────────────────

interface SpecPanelProps {
  spec: CognitiveSpec;
  statuses: Partial<Record<ComponentKey, ComponentStatus>>;
  readyToSave: boolean;
  agentName: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  saving: boolean;
  savedWebhookUrl: string | null;
  testInput: string;
  onTestInputChange: (v: string) => void;
  onRunTest: () => void;
  testRunning: boolean;
}

const CognitiveSpecPanel: React.FC<SpecPanelProps> = ({ spec, statuses, readyToSave, agentName, onNameChange, onSave, saving, savedWebhookUrl, testInput, onTestInputChange, onRunTest, testRunning }) => {
  const completedRequired = COGNITIVE_COMPONENTS.filter(c => c.required && statuses[c.key] === 'complete').length;
  const totalRequired = COGNITIVE_COMPONENTS.filter(c => c.required).length;
  const progress = Math.round((completedRequired / totalRequired) * 100);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 10px', borderBottom: glassDivider, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BuildOutlined style={{ color: '#fff', fontSize: 12 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#141414', lineHeight: 1.2 }}>Agente Cognitivo</div>
            <div style={{ fontSize: 10, color: '#8c8c8c' }}>{completedRequired}/{totalRequired} componentes requeridos</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 10, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
          <div style={{
            height: '100%', borderRadius: 2,
            width: `${progress}%`,
            background: progress === 100 ? '#52c41a' : '#6d28d9',
            transition: 'width 0.4s ease',
          }} />
        </div>
      </div>

      {/* Components list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {COGNITIVE_COMPONENTS.map(({ key, label, emoji, required }) => {
          const status = statuses[key] ?? 'pending';
          const summary = specSummary(key, spec);
          return (
            <div key={key} style={{
              padding: '8px 14px',
              borderBottom: glassDivider,
              transition: 'background 0.15s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, lineHeight: 1 }}>{emoji}</span>
                <span style={{ fontWeight: 600, fontSize: 12, color: '#262626', flex: 1 }}>
                  {label}
                  {required && <span style={{ color: '#ff4d4f', marginLeft: 2, fontSize: 10 }}>*</span>}
                </span>
                {status === 'complete'     && <CheckCircleFilled   style={{ color: '#52c41a', fontSize: 13 }} />}
                {status === 'in_progress'  && <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 13 }} />}
                {status === 'pending'      && <MinusCircleOutlined style={{ color: '#d9d9d9', fontSize: 13 }} />}
              </div>
              {summary && (
                <div style={{
                  fontSize: 10, color: '#8c8c8c', marginTop: 3, paddingLeft: 22,
                  fontStyle: status === 'complete' ? 'normal' : 'italic',
                }}>
                  {summary}
                </div>
              )}
              {!summary && status === 'pending' && (
                <div style={{ fontSize: 10, color: '#d9d9d9', marginTop: 3, paddingLeft: 22 }}>
                  —
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save section */}
      <div style={{ padding: '12px 14px', borderTop: glassDivider, flexShrink: 0 }}>
        {savedWebhookUrl ? (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#52c41a' }}>Agente activo</span>
            </div>
            <div style={{ fontSize: 10, color: '#8c8c8c' }}>Webhook URL:</div>
            <div style={{
              fontSize: 10, color: '#6d28d9', fontFamily: 'monospace',
              background: '#f5f3ff', padding: '4px 8px', borderRadius: 4,
              wordBreak: 'break-all', cursor: 'pointer',
            }}
              onClick={() => { navigator.clipboard.writeText(savedWebhookUrl); antMessage.success('URL copiada'); }}
              title="Clic para copiar"
            >
              {savedWebhookUrl}
            </div>

            {/* ── Test section ── */}
            <div style={{ borderTop: glassDivider, paddingTop: 10, marginTop: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#262626', marginBottom: 6 }}>
                🧪 Prueba de conexión
              </div>
              <Input.TextArea
                size="small"
                rows={4}
                placeholder="Escribe una entrada de prueba..."
                value={testInput}
                onChange={e => onTestInputChange(e.target.value)}
                style={{ fontSize: 11, marginBottom: 6, resize: 'vertical' }}
              />
              <Button
                block
                size="small"
                loading={testRunning}
                disabled={!testInput.trim() || testRunning}
                onClick={onRunTest}
                icon={<ThunderboltOutlined />}
                style={{
                  fontSize: 11, fontWeight: 600,
                  background: testInput.trim() ? '#f3e8ff' : undefined,
                  borderColor: testInput.trim() ? '#d8b4fe' : undefined,
                  color: testInput.trim() ? '#6d28d9' : undefined,
                }}
              >
                Ejecutar prueba
              </Button>
            </div>
          </Space>
        ) : readyToSave ? (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Input
              size="small"
              placeholder="Nombre del agente"
              value={agentName}
              onChange={e => onNameChange(e.target.value)}
              style={{ fontSize: 12 }}
            />
            <Button
              type="primary"
              block
              size="small"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!agentName.trim()}
              onClick={onSave}
              style={{ background: '#6d28d9', borderColor: '#6d28d9', fontSize: 12 }}
            >
              Crear Agente
            </Button>
          </Space>
        ) : (
          <div style={{ fontSize: 10, color: '#bfbfbf', textAlign: 'center', lineHeight: 1.5 }}>
            Configura los componentes requeridos
            <br />en el chat para habilitar la creación
          </div>
        )}
      </div>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

// ── Tools panel ──────────────────────────────────────────────────────────────
// Lives to the right of the chat. Minimizes to a slim rail (icon + count) so
// the chat can take the space back; the choice is remembered per browser.

interface ToolsPanelProps {
  groups: ToolGroup[];
  loading: boolean;
  total: number;
  isBuilder: boolean;
  collapsed: boolean;
  onToggle: () => void;
}

// Memoized: the chat re-renders on every keystroke and the tool list can't change meanwhile.
// Only effective because `onToggle` is a stable useCallback in PlaygroundPage.
const ToolsPanel = React.memo(function ToolsPanel({ groups, loading, total, isBuilder, collapsed, onToggle }: ToolsPanelProps) {
  if (collapsed) {
    return (
      <GlassPanel width={52}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '12px 0' }}>
          <Tooltip title="Mostrar tools" placement="left">
            <Button
              type="text"
              size="small"
              icon={<LeftOutlined />}
              aria-label="Mostrar panel de tools"
              aria-expanded={false}
              onClick={onToggle}
            />
          </Tooltip>
          <Tooltip title={total === 1 ? '1 tool disponible' : `${total} tools disponibles`} placement="left">
            <Badge count={total} size="small" color="#1677ff" overflowCount={99}>
              <ThunderboltOutlined style={{ color: '#1677ff', fontSize: 18 }} />
            </Badge>
          </Tooltip>
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel width={isBuilder ? 240 : 280}>
      <div style={{ padding: '14px 16px 10px', borderBottom: glassDivider, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
            <ThunderboltOutlined style={{ color: '#1677ff', fontSize: 13 }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: '#141414', whiteSpace: 'nowrap' }}>Tools disponibles</span>
          </div>
          {total > 0 && (
            <Tag color="blue" style={{ margin: 0, fontSize: 11, padding: '0 6px' }}>{total}</Tag>
          )}
          <Tooltip title="Minimizar" placement="left">
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              aria-label="Minimizar panel de tools"
              aria-expanded={true}
              onClick={onToggle}
            />
          </Tooltip>
        </div>
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 3 }}>
          {isBuilder ? 'El agente puede usar estas tools' : 'El LLM puede usar estas tools libremente'}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {loading && <div style={{ textAlign: 'center', padding: 24 }}><Spin size="small" /></div>}
        {!loading && groups.length === 0 && (
          <div style={{ padding: '16px', fontSize: 12, color: '#8c8c8c', textAlign: 'center' }}>
            No hay tools configuradas.<br />Añade un conector en la página de Conectores.
          </div>
        )}
        {groups.map(group => {
          const style = connectorStyle(group.type);
          return (
            <div key={group.id} style={{ marginBottom: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px 4px' }}>
                <span style={{ color: style.color, fontSize: 13 }}>{style.icon}</span>
                <span style={{ fontWeight: 600, fontSize: 12, color: '#262626' }}>{group.name}</span>
                {group.source === 'env' && (
                  <Tag color="cyan" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', marginLeft: 'auto' }}>ENV</Tag>
                )}
              </div>
              <div style={{ paddingLeft: 16, paddingRight: 12 }}>
                {group.tools.map(tool => (
                  <Tooltip key={tool.name} title={tool.description} placement="left">
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 6,
                      padding: '5px 8px', marginBottom: 2,
                      cursor: 'default', ...glassInsetStyle,
                    }}>
                      <ToolOutlined style={{ fontSize: 10, color: '#bfbfbf', marginTop: 3, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#262626', fontFamily: 'monospace' }}>{tool.name}</div>
                        <div style={{
                          fontSize: 10, color: '#8c8c8c', lineHeight: 1.4,
                          display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {tool.description}
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: '8px 14px', borderTop: glassDivider, fontSize: 10, color: '#bfbfbf', flexShrink: 0 }}>
        {isBuilder ? '← Disponibles en tu agente' : '← Pide al LLM qué tools tiene disponibles'}
      </div>
    </GlassPanel>
  );
});

const PlaygroundPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editAgentId = searchParams.get('agent_id');

  const [mode, setMode]               = useState<PlaygroundMode>('chat');
  const [toolGroups, setToolGroups]   = useState<ToolGroup[]>([]);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [toolsCollapsed, setToolsCollapsed] = useState<boolean>(() => readFlag(TOOLS_COLLAPSED_KEY));
  const [messages, setMessages]       = useState<ChatMessage[]>([]);
  const [inputText, setInputText]     = useState('');
  const [sending, setSending]         = useState(false);

  // Builder mode state
  const [cogSpec, setCogSpec]                   = useState<CognitiveSpec>({});
  const [componentStatuses, setComponentStatuses] = useState<Partial<Record<ComponentKey, ComponentStatus>>>({});
  const [readyToSave, setReadyToSave]           = useState(false);
  const [agentName, setAgentName]               = useState('');
  const [saving, setSaving]                     = useState(false);
  const [savedWebhookUrl, setSavedWebhookUrl]   = useState<string | null>(null);

  const [testInput, setTestInput]       = useState('');
  const [testRunning, setTestRunning]   = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef  = useRef<HTMLDivElement>(null);

  // Load existing cognitive agent when agent_id is in URL
  useEffect(() => {
    if (!editAgentId) return;
    fetch(`${BUILDER_API}/agent/builder/agents`)
      .then(r => r.json())
      .then(data => {
        const agent = (data.agents ?? []).find((a: { id: string }) => a.id === editAgentId);
        if (!agent?.cognitiveSpec) return;
        const loadedSpec = agent.cognitiveSpec as CognitiveSpec;
        setCogSpec(loadedSpec);
        setAgentName(agent.name ?? '');
        setSavedWebhookUrl(`/agent/cognitive/${editAgentId}/run`);
        setTestInput(sampleInputFor(loadedSpec));
        setMode('builder');
        const statuses: Partial<Record<ComponentKey, ComponentStatus>> = {};
        (Object.keys(agent.cognitiveSpec) as ComponentKey[]).forEach(k => {
          statuses[k] = 'complete';
        });
        setComponentStatuses(statuses);
        setReadyToSave(true);
      })
      .catch(() => antMessage.error('No se pudo cargar el agente'));
  }, [editAgentId]);

  useEffect(() => {
    fetch(`${EXECUTOR_API}/agent/builder/playground/tools`)
      .then(r => r.json())
      .then(d => setToolGroups(d.groups || []))
      .catch(() => antMessage.error('No se pudieron cargar las tools'))
      .finally(() => setToolsLoading(false));
  }, []);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleModeChange = (val: string | number) => {
    const newMode = val as PlaygroundMode;
    setMode(newMode);
    setMessages([]);
    setInputText('');
    if (newMode === 'builder') {
      setCogSpec({});
      setComponentStatuses({});
      setReadyToSave(false);
      setAgentName('');
    }
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const sendMessage = useCallback(async () => {
    const text = inputText.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = { role: 'user', content: text };
    const history = [...messages, userMsg];
    setMessages(history);
    setInputText('');
    setSending(true);

    try {
      let url: string;
      let body: Record<string, unknown>;

      if (mode === 'builder') {
        url  = `${EXECUTOR_API}/agent/cognitive/build`;
        body = {
          messages: history.map(m => ({ role: m.role, content: m.content })),
          current_spec: cogSpec,
        };
      } else {
        url  = `${EXECUTOR_API}/agent/builder/playground/chat`;
        body = { messages: history.map(m => ({ role: m.role, content: m.content })) };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Error desconocido' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();

      if (mode === 'builder') {
        // Builder response: { reply, partial_spec, component_statuses, ready_to_save }
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply ?? data.message ?? '' }]);
        if (data.partial_spec) {
          setCogSpec(data.partial_spec);
          if (!agentName && data.partial_spec.goal?.objective) {
            setAgentName(data.partial_spec.goal.objective.slice(0, 60));
          }
        }
        if (data.component_statuses)  setComponentStatuses(data.component_statuses);
        if (data.ready_to_save)       setReadyToSave(true);
      } else {
        // Chat response: { message, tool_calls }
        setMessages(prev => [...prev, { role: 'assistant', content: data.message, tool_calls: data.tool_calls }]);
      }
    } catch (e: unknown) {
      antMessage.error(e instanceof Error ? e.message : 'Error desconocido');
      setMessages(prev => prev.slice(0, -1));
      setInputText(text);
    } finally {
      setSending(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [inputText, sending, messages, mode, cogSpec, agentName]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleSaveAgent = async () => {
    if (!agentName.trim()) return;
    setSaving(true);
    try {
      const isEdit = !!editAgentId;
      const url = isEdit
        ? `${EXECUTOR_API}/agent/cognitive/${editAgentId}`
        : `${EXECUTOR_API}/agent/cognitive/save`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: agentName.trim(), spec: cogSpec }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Error desconocido' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSavedWebhookUrl(data.webhook_url);
      setTestInput(sampleInputFor(cogSpec));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isEdit
          ? `¡Listo! El agente **${agentName}** ha sido actualizado. ¿Quieres hacer una **prueba de conexión**? He preparado un ejemplo en el panel de la derecha — pulsa "Ejecutar prueba" para ver cómo responde.`
          : `¡Perfecto! El agente **${agentName}** ya está activo 🎉\n\n¿Quieres hacer una **prueba de conexión** para verificar que todo funciona? He preparado una entrada de ejemplo en el panel de la derecha — solo pulsa "Ejecutar prueba".`,
      }]);
      antMessage.success(
        isEdit ? `Agente "${agentName}" actualizado ✓` : `Agente "${agentName}" creado y activo ✓`,
        5,
      );
    } catch (e: unknown) {
      antMessage.error(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  function sampleInputFor(spec: CognitiveSpec): string {
    switch (spec.sense?.input_type) {
      case 'meeting_transcript':
        return 'Transcripción de reunión de prueba:\n\nCliente: Me gustaría saber el precio de la reforma completa del piso.\nComercial: Claro, ¿cuántos metros cuadrados tiene?\nCliente: Unos 90m2. ¿Cuánto tiempo tardaría?';
      case 'aircall_webhook':
        return JSON.stringify({ event: 'call.ended', call: { duration: 240, transcript: 'Cliente pregunta por precio y plazos de reforma' } }, null, 2);
      case 'json':
        return '{\n  "data": "valor de prueba"\n}';
      default:
        return 'Texto de prueba para verificar que el agente responde correctamente.';
    }
  }

  const handleRunTest = async () => {
    if (!savedWebhookUrl || !testInput.trim()) return;
    setTestRunning(true);
    try {
      const fullUrl = savedWebhookUrl.startsWith('http')
        ? savedWebhookUrl
        : `${EXECUTOR_API}${savedWebhookUrl}`;
      const res = await fetch(fullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: testInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      const resultJson = JSON.stringify(data.result ?? data, null, 2);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**✅ Prueba completada.** Aquí tienes el resultado:\n\`\`\`json\n${resultJson}\n\`\`\``,
      }]);
      antMessage.success('Prueba ejecutada correctamente');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error desconocido';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**❌ Error en la prueba:** ${msg}`,
      }]);
      antMessage.error(msg);
    } finally {
      setTestRunning(false);
    }
  };

  const toggleToolsPanel = useCallback(() => {
    const next = !toolsCollapsed;
    setToolsCollapsed(next);
    writeFlag(TOOLS_COLLAPSED_KEY, next);
  }, [toolsCollapsed]);

  const totalTools = toolGroups.reduce((n, g) => n + g.tools.length, 0);
  const isBuilder  = mode === 'builder';

  const welcomeText = isBuilder
    ? '¡Hola! Soy tu asistente para configurar agentes cognitivos. Cuéntame: ¿qué quieres que haga el agente? Puedes describirlo con tus propias palabras.'
    : `Hola, tengo acceso a ${totalTools} tool${totalTools !== 1 ? 's' : ''}. ¿En qué te puedo ayudar?`;

  const welcomeColor = isBuilder
    ? 'linear-gradient(135deg, #6d28d9, #a78bfa)'
    : 'linear-gradient(135deg, #166534, #22c55e)';

  const inputPlaceholder = isBuilder
    ? 'Describe el agente que quieres crear… (Enter para enviar)'
    : 'Escribe un mensaje… (Enter para enviar, Shift+Enter para salto de línea)';

  return (
    <GlassWorkspace>

        {/* ── Chat area ── */}
        <GlassPanel>

          {/* Mode toggle bar */}
          <div style={{ ...glassBarStyle, padding: '8px 20px', gap: 16 }}>
            <Segmented
              size="small"
              value={mode}
              onChange={handleModeChange}
              options={[
                { value: 'chat',    label: <span style={{ fontSize: 12 }}><MessageOutlined style={{ marginRight: 5 }} />Chat Libre</span> },
                { value: 'builder', label: <span style={{ fontSize: 12 }}><BuildOutlined style={{ marginRight: 5 }} />Modo Builder</span> },
              ]}
              style={{
                background: isBuilder ? '#f3e8ff' : undefined,
              }}
            />
            {isBuilder && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Conversa para configurar tu agente cognitivo · el panel derecho se actualiza en tiempo real
              </Text>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 8px' }}>
            {messages.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                marginTop: 60, gap: 12,
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: welcomeColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(109,40,217,0.25)',
                }}>
                  {isBuilder
                    ? <BuildOutlined  style={{ color: '#fff', fontSize: 22 }} />
                    : <RobotOutlined  style={{ color: '#fff', fontSize: 22 }} />
                  }
                </div>
                <Text type="secondary" style={{ fontSize: 14, textAlign: 'center', maxWidth: 380 }}>
                  {welcomeText}
                </Text>
                {isBuilder && (
                  <div style={{
                    display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4,
                  }}>
                    {['Agente de análisis de reuniones', 'Extractor de presupuestos', 'Clasificador de leads'].map(s => (
                      <button
                        key={s}
                        onClick={() => setInputText(s)}
                        style={{
                          background: '#f3e8ff', border: '1px solid #d8b4fe',
                          borderRadius: 20, padding: '5px 12px',
                          fontSize: 11, color: '#6d28d9', cursor: 'pointer',
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {messages.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start', gap: 8,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #1e3a8a, #3b82f6)'
                      : welcomeColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {msg.role === 'user'
                      ? <UserOutlined  style={{ color: '#fff', fontSize: 13 }} />
                      : isBuilder
                        ? <BuildOutlined style={{ color: '#fff', fontSize: 13 }} />
                        : <RobotOutlined  style={{ color: '#fff', fontSize: 13 }} />
                    }
                  </div>

                  <div style={{ maxWidth: '72%' }}>
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                      background: msg.role === 'user' ? '#1677ff' : '#fff',
                      color: msg.role === 'user' ? '#fff' : '#141414',
                      fontSize: 13, lineHeight: 1.65,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      whiteSpace: msg.role === 'user' ? 'pre-wrap' : undefined,
                      wordBreak: 'break-word',
                    }}>
                      {msg.role === 'assistant' && msg.content
                        ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                        : msg.content || <em style={{ opacity: 0.4 }}>(sin respuesta)</em>
                      }
                    </div>

                    {msg.tool_calls && msg.tool_calls.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <Collapse ghost size="small">
                          {msg.tool_calls.map((tc, j) => (
                            <Panel
                              key={j}
                              header={
                                <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                  <ToolOutlined style={{ marginRight: 5, color: '#fa8c16' }} />
                                  <code style={{ fontSize: 11, fontWeight: 600 }}>{tc.name}</code>
                                  <span style={{ marginLeft: 8, opacity: 0.5 }}>→ ver resultado</span>
                                </span>
                              }
                              style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, marginBottom: 4 }}
                            >
                              <div style={{ fontSize: 11 }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>Args:</Text>
                                <pre style={{ fontSize: 10, background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, margin: '2px 0 8px', overflowX: 'auto' }}>
                                  {JSON.stringify(tc.args, null, 2)}
                                </pre>
                                <Text type="secondary" style={{ fontSize: 11 }}>Result:</Text>
                                <pre style={{ fontSize: 10, background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, margin: '2px 0 0', overflowX: 'auto', maxHeight: 240, overflowY: 'auto' }}>
                                  {typeof tc.result === 'string' ? tc.result : JSON.stringify(tc.result, null, 2)}
                                </pre>
                              </div>
                            </Panel>
                          ))}
                        </Collapse>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: welcomeColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isBuilder ? <BuildOutlined style={{ color: '#fff', fontSize: 13 }} /> : <RobotOutlined style={{ color: '#fff', fontSize: 13 }} />}
                  </div>
                  <div style={{
                    padding: '10px 16px', background: '#fff', borderRadius: '4px 16px 16px 16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <Spin size="small" />
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {isBuilder ? 'Configurando…' : 'Pensando…'}
                    </span>
                  </div>
                </div>
              )}
            </Space>

            <div ref={chatEndRef} />
          </div>

          {/* Input bar */}
          <div style={{ padding: '12px 32px 16px', borderTop: glassDivider, flexShrink: 0 }}>
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-end',
              background: glass.surfaceStrong, borderRadius: 12, padding: '8px 12px',
              border: `1px solid ${isBuilder ? '#d8b4fe' : glass.border}`,
              transition: 'border-color 0.2s',
            }}>
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                disabled={sending}
                rows={1}
                style={{
                  flex: 1, border: 'none', outline: 'none', resize: 'none',
                  background: 'transparent', fontSize: 13, lineHeight: 1.6,
                  maxHeight: 120, overflowY: 'auto', fontFamily: 'inherit', color: '#141414',
                }}
                onInput={e => {
                  const el = e.currentTarget;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim() || sending}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  background: !inputText.trim() || sending ? '#e8e8e8' : isBuilder ? '#6d28d9' : '#1677ff',
                  color: !inputText.trim() || sending ? '#bfbfbf' : '#fff',
                  cursor: !inputText.trim() || sending ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.15s',
                }}
              >
                <SendOutlined style={{ fontSize: 13 }} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#bfbfbf', textAlign: 'center', marginTop: 6 }}>
              Enter para enviar · Shift+Enter para nueva línea
            </div>
          </div>
        </GlassPanel>

        {/* ── Tools panel (right of the chat, collapsible) ── */}
        <ToolsPanel
          groups={toolGroups}
          loading={toolsLoading}
          total={totalTools}
          isBuilder={isBuilder}
          collapsed={toolsCollapsed}
          onToggle={toggleToolsPanel}
        />

        {/* ── Cognitive spec panel (builder mode only) ── */}
        {isBuilder && (
          <GlassPanel width={270}>
            <CognitiveSpecPanel
              spec={cogSpec}
              statuses={componentStatuses}
              readyToSave={readyToSave}
              agentName={agentName}
              onNameChange={setAgentName}
              onSave={handleSaveAgent}
              saving={saving}
              savedWebhookUrl={savedWebhookUrl}
              testInput={testInput}
              onTestInputChange={setTestInput}
              onRunTest={handleRunTest}
              testRunning={testRunning}
            />
          </GlassPanel>
        )}

    </GlassWorkspace>
  );
};

export default PlaygroundPage;
